import { CommonModule } from '@angular/common';
import { Component, OnInit, effect, inject, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ToastService } from 'angular-toastify';
import { ParkingGate, ParkingGateDto } from '../../../../core/models/system-settings/parking-master-data.model';
import { MapperParkingMasterDataService } from '../../../../core/mappers/system-settings/mapper-parking-master-data.service';
import { ApiParkingMasterDataService } from '../../../../core/services/api/system-settings/api-parking-master-data.service';
import { MenuPermissionService } from '../../../../core/services/menu-permission.service';
import { SiteStateService } from '../../../../core/services/site-state.service';
import { PssLoadingComponent } from '../../../../tools/template/pss/pss-loading/pss-loading.component';
import { ModalParkingGateComponent } from '../../modals/system-settings/modal-parking-gate/modal-parking-gate.component';

@Component({
  selector: 'app-parking-gate',
  standalone: true,
  imports: [CommonModule, FormsModule, PssLoadingComponent, ModalParkingGateComponent],
  templateUrl: './parking-gate.component.html',
  styleUrl: './parking-gate.component.css',
})
export class ParkingGateComponent implements OnInit {
  readonly siteState = inject(SiteStateService);
  private readonly api = inject(ApiParkingMasterDataService);
  private readonly mapper = inject(MapperParkingMasterDataService);
  private readonly toast = inject(ToastService);
  private readonly router = inject(Router);
  private readonly permissionService = inject(MenuPermissionService);

  items = signal<ParkingGate[]>([]);
  isLoading = signal(false);
  isModalOpen = signal(false);
  selectedId = signal(0);
  searchText = signal('');
  currentPage = signal(1);
  pageSize = signal(20);
  pageSizeOptions: number[] = [10, 20, 50, 100];
  totalItems = signal(0);
  totalPages = signal(1);

  constructor() {
    effect(() => {
      const site = this.siteState.selectedSite();
      if (!site?.id) return;
      untracked(() => {
        this.currentPage.set(1);
        this.loadTable();
      });
    });
  }

  get selectedSiteId(): number { return this.siteState.selectedSite()?.id ?? 0; }
  get canCreate() { return this.permissionService.canCreate(this.router.url); }
  get canUpdate() { return this.permissionService.canUpdate(this.router.url); }

  get startItemIndex(): number {
    if (this.totalItems() === 0) return 0;
    return (this.currentPage() - 1) * this.pageSize() + 1;
  }

  get endItemIndex(): number {
    return Math.min(this.currentPage() * this.pageSize(), this.totalItems());
  }

  get pagesArray(): number[] {
    const maxVisibleButtons = 5;
    let startPage = Math.max(1, this.currentPage() - 2);
    let endPage = Math.min(this.totalPages(), startPage + maxVisibleButtons - 1);
    if (endPage - startPage < maxVisibleButtons - 1) {
      startPage = Math.max(1, endPage - maxVisibleButtons + 1);
    }
    return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
  }

  ngOnInit(): void {
    if (this.selectedSiteId) this.loadTable();
  }

  onSearchChange(): void { this.currentPage.set(1); this.loadTable(); }
  onRefresh(): void { this.currentPage.set(1); this.loadTable(); }

  openCreate(): void {
    if (!this.canCreate) { this.toast.error('ไม่มีสิทธิ์สร้าง'); return; }
    if (!this.selectedSiteId) { this.toast.error('กรุณาเลือกลานจาก Sidebar'); return; }
    this.selectedId.set(0);
    this.isModalOpen.set(true);
  }

  openEdit(item: ParkingGate): void {
    if (!this.canUpdate) { this.toast.error('ไม่มีสิทธิ์แก้ไข'); return; }
    this.selectedId.set(item.id);
    this.isModalOpen.set(true);
  }

  closeModal(): void { this.isModalOpen.set(false); this.selectedId.set(0); }

  setPage(page: number): void {
    if (page >= 1 && page <= this.totalPages()) {
      this.currentPage.set(page);
      this.loadTable();
    }
  }

  onPageSizeChange(event: Event): void {
    const element = event.target as HTMLSelectElement;
    this.pageSize.set(Number(element.value));
    this.currentPage.set(1);
    this.loadTable();
  }

  trackById(_: number, item: ParkingGate): number { return item.id; }

  loadTable(): void {
    const siteId = this.selectedSiteId;
    if (!siteId) { this.items.set([]); return; }

    this.isLoading.set(true);
    this.api.getParkingGateForTable({
      page: this.currentPage(),
      pageSize: this.pageSize(),
      searchText: this.searchText().trim() || undefined,
      siteId,
    }).subscribe({
      next: (res) => {
        const mapped = this.mapper.mapTable<ParkingGateDto, ParkingGate>(
          res,
          (item) => this.mapper.mapGate(item)
        );
        this.items.set(mapped.data);
        this.totalItems.set(mapped.total);
        this.totalPages.set(Math.max(1, Math.ceil(mapped.total / this.pageSize())));
        this.isLoading.set(false);
      },
      error: () => {
        this.items.set([]);
        this.isLoading.set(false);
        this.toast.error('โหลดข้อมูลล้มเหลว');
      },
    });
  }
}
